if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.confirmedUnavailable = false;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.restoreSubmitButtonState();
        this.error = false;
        this.confirmedUnavailable = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              const errorMessage = response.description || response.message || 'Shopify could not add this item to your bag.';
              const [verifiedAvailability, cartQuantity] = await Promise.all([
                this.verifyVariantAvailability(variantId),
                this.getVariantCartQuantity(variantId),
              ]);

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(this.getCustomerCartError(errorMessage, verifiedAvailability, cartQuantity));
              this.dispatchCartErrorEvent(errorMessage, 'INVALID');
              linesUpdateDeferred?.reject(new Error(errorMessage));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              this.confirmedUnavailable = verifiedAvailability === false;

              if (this.confirmedUnavailable && soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                if (soldOutMessage.dataset.soldOutText && !soldOutMessage.textContent.trim()) {
                  soldOutMessage.textContent = soldOutMessage.dataset.soldOutText;
                }
                soldOutMessage.classList.remove('hidden');
              } else {
                this.restoreSubmitButtonState();
              }

              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);
            this.confirmedUnavailable = false;

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.confirmedUnavailable = false;
            this.restoreSubmitButtonState();
            this.handleErrorMessage('Shopify could not add this item to your bag. Please try again.');
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.confirmedUnavailable) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      restoreSubmitButtonState() {
        const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
        this.submitButtonText?.classList.remove('hidden');
        soldOutMessage?.classList.add('hidden');
      }

      getCustomerCartError(errorMessage, verifiedAvailability, cartQuantity = 0) {
        const message = String(errorMessage || '').trim();
        const inventoryError = /sold out|out of stock|not available|unavailable/i.test(message);
        const quantityError = inventoryError || /can(?:not|'t) add more|maximum|already.*cart|all.*cart/i.test(message);

        if (verifiedAvailability === true && cartQuantity > 0 && quantityError) {
          return 'Item is already in the bag. Maximum available quantity at The Cove has been reached. If you need more, please call.';
        }

        if (verifiedAvailability === true && inventoryError) {
          return 'This item is still listed as available, but Shopify could not add it to your bag. Please refresh the page and try again.';
        }

        return message || 'Shopify could not add this item to your bag. Please try again.';
      }

      async verifyVariantAvailability(variantId) {
        const productUrl = this.dataset.productUrl;
        if (!productUrl || !variantId) return null;

        try {
          const availabilityUrl = new URL(productUrl, window.location.origin);
          availabilityUrl.pathname = `${availabilityUrl.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
          availabilityUrl.search = '';

          const response = await fetch(availabilityUrl.toString(), {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });

          if (!response.ok) return null;

          const product = await response.json();
          const variant = product?.variants?.find((item) => String(item.id) === String(variantId));
          return typeof variant?.available === 'boolean' ? variant.available : null;
        } catch (error) {
          console.warn('[The Cove] Variant availability could not be rechecked.', error);
          return null;
        }
      }

      async getVariantCartQuantity(variantId) {
        if (!variantId) return 0;

        try {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });

          if (!response.ok) return 0;

          const cart = await response.json();
          return (cart?.items || [])
            .filter((item) => String(item.variant_id) === String(variantId))
            .reduce((quantity, item) => quantity + Number(item.quantity || 0), 0);
        } catch (error) {
          console.warn('[The Cove] Cart quantity could not be rechecked.', error);
          return 0;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
